<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :unit-options="unitOptions"
          :unit-filter.sync="filters.units"
          :user-options="userOptions"
          :country-options="countryOptions"
          :municipality-options="municipalityOptions"
          :status-options="statusOptions"
          :form-type-options="formTypeOptions"
          :user-filter.sync="filters.users"
          :status-filter.sync="filters.status"
          :form-type-filter.sync="filters.formType"
          :responsable-filter.sync="filters.responsable"
          :assigned-filter.sync="filters.assigned"
          :start-date.sync="filters.startDate"
          :end-date.sync="filters.endDate"
          :country-filter.sync="filters.countries"
          :municipality-filter.sync="filters.municipalities"
          :rems-filter.sync="filters.rems"
          :sirh-filter.sync="filters.sirh"
        />
      </template>
    </filter-modal>

    <b-card class="mx-2" no-body>
      <b-row class="m-2">
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label class="text-capitalize">{{ $t("table.show") }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block mx-50"
          />
          <label class="text-capitalize">{{ $t("table.entries") }}</label>
        </b-col>
        <b-col cols="12" md="6">
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t('table.search') + '...'"
            />
            <b-button
              variant="secondary"
              @click="openAdd()"
              v-if="$can('create', 'BauComplement')"
            >
              <span class="text-nowrap text-capitalize">{{
                $t("actions.add") + " " + $t("bau_complement.title")
              }}</span>
            </b-button>
            <b-button
              @click="exportExcel()"
              variant="success"
              class="text-capitalize ml-1"
              type="button"
              :disabled="buttonExport"
            >
              <feather-icon icon="DownloadCloudIcon" size="15" />
            </b-button>
          </div>
        </b-col>
      </b-row>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #cell(last_status.name)="data">
          <span>{{ formatStatus(data.item.last_status && data.item.last_status.name) }}</span>
        </template>

        <template #cell(form_type)="data">
          <span>{{ getFormType(data.item) }}</span>
        </template>

        <template #cell(unit.rems)="data">
          <span>{{ getDisplayValue(data.item && data.item.unit ? data.item.unit.rems : null) }}</span>
        </template>

        <template #cell(unit.sirh)="data">
          <span>{{ getDisplayValue(data.item && data.item.unit ? data.item.unit.sirh : null) }}</span>
        </template>

        <template #cell(assigned_to)="data">
          <div>
            <div>{{ getUserPrimary(data.item.assigned_to) }}</div>
            <small class="text-muted">{{ getUserSecondary(data.item.assigned_to) }}</small>
          </div>
        </template>

        <template #cell(in_review_by)="data">
          <div>
            <div>{{ getUserPrimary(data.item.in_review_by) }}</div>
            <small class="text-muted">{{ getUserSecondary(data.item.in_review_by) }}</small>
          </div>
        </template>

        <template #cell(accepted_or_cancelled_by)="data">
          <div>
            <div>{{ getUserPrimary(data.item.accepted_or_cancelled_by) }}</div>
            <small class="text-muted">
              {{ getUserSecondary(data.item.accepted_or_cancelled_by) }}
            </small>
          </div>
        </template>

        <template #cell(created_at)="data">
          <span>{{ formatDateAssigned(data.item.created_at) }}</span>
        </template>

        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="canShowStartForm(data.item)"
              @click="openStartFormFromRow(data.item)"
            >
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("bau_complement.start_form") }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="!canShowStartForm(data.item)"
              @click.prevent="showEditModal(data.item)"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50 text-capitalize">{{ $t("actions.edit") }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="downloadPdfAndFilesLinear(data.item.id)"
              v-if="canShowDownload(data.item)"
            >
              <feather-icon icon="FileIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("actions.download") }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="confirmDelete(data.item.id)"
              v-if="canShowCancelAction(data.item)"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("actions.cancel") }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">
              {{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>

    <ShowBauComplementFormModal
      v-if="modalId"
      :key="modalId"
      :bau-complement-id="modalId"
      :template-snapshot="modalSnapshot"
      :template-source-ids="modalSources"
      @close="onModalClose"
    />

    <bau-complement-wizard
      :prefill="wizardPrefill"
      @open-bau-complement-modal="handleOpen"
      @wizard-complete="onWizardComplete"
      @wizard-closed="onWizardClosed"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BDropdown,
  BDropdownItem,
  BTable,
} from "bootstrap-vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import { ref, getCurrentInstance, onMounted, watch } from "@vue/composition-api";
import vSelect from "vue-select";
import Swal from "sweetalert2";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Pagination from "@/components/Pagination.vue";
import useList from "./useList";
import Filters from "../evaluations/Filters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import ShowBauComplementFormModal from "@/components/ShowBauComplementFormModal.vue";
import BauComplementWizard from "./BauComplementWizard.vue";
import { canShowScheduledStartForm } from "@/libs/risk/scheduledStartFormAccess";

export default {
  name: "IndexBauComplements",
  components: {
    BreadcrumbMenu,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BDropdown,
    BDropdownItem,
    BTable,
    Pagination,
    vSelect,
    ToastificationContent,
    Filters,
    FilterModal,
    ButtonFilters,
    ShowBauComplementFormModal,
    BauComplementWizard,
  },
  setup() {
    const vm = getCurrentInstance().proxy;
    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      unitOptions,
      userOptions,
      filters,
      filtersCount,
      buildFilters,
      countryOptions,
      municipalityOptions,
      clearSearchFilters,
    } = useList();

    const toast = useToast();
    const modalId = ref(null);
    const modalSnapshot = ref(null);
    const modalSources = ref(null);
    const wizardPrefill = ref(null);
    const buttonExport = ref(false);
    const canShowStartForm = canShowScheduledStartForm;

    const statusOptions = [
      { label: vm.$t("risk.status_in_progress"), value: "in_progress" },
      { label: vm.$t("risk.status_in_review"), value: "in_review" },
      { label: vm.$t("risk.status_approved"), value: "approved" },
      { label: vm.$t("risk.status_canceled"), value: "canceled" },
      { label: vm.$t("risk.status_reassigned"), value: "reassigned" },
      { label: vm.$t("risk.status_rejected"), value: "rejected" },
    ];
    const formTypeOptions = [
      {
        label: "Revisión CCTV Sucursales",
        value: "Revisión CCTV Sucursales",
      },
      {
        label: "Attestation Mensual Revisiones Diarias CCTV Edificios",
        value: "Attestation Mensual Revisiones Diarias CCTV Edificios",
      },
      {
        label: "Attestation Revisiones Trimestrales CCTV Sucursales",
        value: "Attestation Revisiones Trimestrales CCTV Sucursales",
      },
      {
        label: "Attestation Revisiones Trimestrales CCTV Local Edificios",
        value: "Attestation Revisiones Trimestrales CCTV Local Edificios",
      },
    ];

    const openStartFormFromRow = async (row) => {
      const unitId =
        row.id_unit != null
          ? Number(row.id_unit)
          : row.unit && row.unit.id != null
          ? Number(row.unit.id)
          : null;
      if (unitId == null || !Number.isFinite(unitId)) return;
      const lastStatusName =
        row.last_status && row.last_status.name != null
          ? String(row.last_status.name).trim()
          : "";
      wizardPrefill.value = {
        bauComplementId: Number(row.id),
        referenceNumber: row.reference_number || "",
        unitId,
        ...(lastStatusName ? { existingRecordLastStatusName: lastStatusName } : {}),
      };
      await vm.$nextTick();
      vm.$root.$emit("bv::show::modal", "bau-complement-wizard");
    };

    const onWizardClosed = () => {
      wizardPrefill.value = null;
    };

    const showEditModal = async (row) => {
      modalId.value = row.id;
      modalSnapshot.value = null;
      modalSources.value = null;
      await vm.$nextTick();
      vm.$root.$emit("bv::show::modal", "bau_complement_form_modal");
    };

    const openFromNotification = async () => {
      const routeQuery = vm.$route && vm.$route.query ? vm.$route.query : {};
      const idParam = routeQuery.id;
      if (!idParam) return;
      modalId.value = Number(idParam);
      modalSnapshot.value = null;
      modalSources.value = null;
      await vm.$nextTick();
      vm.$root.$emit("bv::show::modal", "bau_complement_form_modal");
      vm.$nextTick(() => {
        if (vm.$route && vm.$route.query && Object.keys(vm.$route.query).length) {
          vm.$router.replace({ path: vm.$route.path, query: {} });
        }
      });
    };

    watch(
      () => (vm.$route && vm.$route.query ? vm.$route.query.id : null),
      async (newId, oldId) => {
        if (newId && newId !== oldId) await openFromNotification();
      }
    );

    const confirmDelete = (id) => {
      Swal.fire({
        title: vm.$t("bau_complement.confirm_cancel"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: vm.$t("actions.cancel"),
        cancelButtonText: vm.$t("actions.exit"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (!result.value) return;
        axios
          .post(`/v1/bau-complements/update_status/${id}`, {
            status: "canceled",
            status_name: "canceled",
          })
          .then(() => {
            refetchData();
            toast({
              component: ToastificationContent,
              props: {
                title: vm.$t("bau_complement.canceled"),
                icon: "CheckIcon",
                variant: "success",
              },
            });
          });
      });
    };

    const handleOpen = async ({ id, snapshot, sources }) => {
      modalId.value = id;
      modalSnapshot.value = Array.isArray(snapshot) ? snapshot : null;
      modalSources.value = Array.isArray(sources) ? sources : null;
      await vm.$nextTick();
      vm.$root.$emit("bv::show::modal", "bau_complement_form_modal");
    };

    const formatStatus = (name) => {
      const raw = String(name || "");
      const normalized = raw.trim().toLowerCase().replace(/[-\s]+/g, "_");
      const aliases = { cancelled: "canceled", accepted: "approved", aceptado: "approved" };
      const canonical = aliases[normalized] || normalized;
      const key = `risk.status_${canonical}`;
      const translated = vm.$t(key);
      if (translated !== key) return translated;
      return raw;
    };

    const normalizeStatusKey = (name) => {
      const raw = String(name || "").trim().toLowerCase().replace(/[-\s]+/g, "_");
      const aliases = { cancelled: "canceled", accepted: "approved", aceptado: "approved" };
      return aliases[raw] || raw;
    };

    const canShowDownload = (row) => {
      const statusName = row?.last_status?.name;
      return normalizeStatusKey(statusName) === "approved";
    };

    const getFormType = (row) => {
      const formType = row?.formAnswer?.data?.["Subcategorías"];
      if (formType == null) return "-";
      if (typeof formType === "string" && !formType.trim()) return "-";
      return formType;
    };

    const getDisplayValue = (value) => {
      if (value == null) return "-";
      if (typeof value === "string" && !value.trim()) return "-";
      return value;
    };

    const canShowCancelAction = (row) => {
      if (!vm.$can("cancel", "BauComplement")) return false;
      if (!row?.last_status?.name) return false;
      if (canShowStartForm(row)) return false;
      const statusName = normalizeStatusKey(row.last_status.name);
      if (["approved", "canceled"].includes(statusName)) return false;
      return true;
    };

    const sanitizeFilename = (value) =>
      String(value || "")
        .replace(/[\/\\:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ")
        .trim();

    const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

    const saveBlobToDisk = (blob, filename) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    const downloadFileByUrl = async (url, filename) => {
      try {
        const response = await axios.get(url, { responseType: "blob" });
        const safeName = sanitizeFilename(filename || url.split("/").pop() || "archivo");
        saveBlobToDisk(response.data, safeName);
      } catch (_) {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener";
        link.click();
      }
    };

    const fetchBauComplementPdf = async (bauComplementId) => {
      const response = await axios.get(`/v1/bau-complements/${bauComplementId}`, {
        params: { is_pdf: true },
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      });
      const contentDisposition = response.headers["content-disposition"];
      let filename = `bau_complement_${bauComplementId}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
        if (match) filename = decodeURIComponent(match[1]);
      }
      return { blob: response.data, filename: sanitizeFilename(filename) };
    };

    const fetchBauComplementDetailsForFiles = async (bauComplementId) => {
      const response = await axios.get(
        `/v1/bau_complement_form_answers/get-form-by-bau-complement/${bauComplementId}`
      );
      return response.data || {};
    };

    const extractAllFileEntriesBauComplement = (payload) => {
      let answers = payload;
      if (answers && typeof answers === "object" && answers.data) {
        answers = answers.data;
      }
      if (answers && typeof answers === "object" && answers.data) {
        answers = answers.data;
      }
      const files = [];
      if (!answers || typeof answers !== "object") return files;
      Object.entries(answers).forEach(([fieldName, value]) => {
        if (!Array.isArray(value)) return;
        value.forEach((file, index) => {
          if (!file || !file.url) return;
          const url = file.url;
          const baseName = file.name || file.filename || `archivo_${index + 1}`;
          const composedName = `${sanitizeFilename(fieldName)} - ${sanitizeFilename(baseName)}`;
          files.push({ url, name: composedName });
        });
      });
      return files;
    };

    const downloadPdfAndFilesLinear = async (bauComplementId) => {
      try {
        const { blob: pdfBlob, filename: pdfName } = await fetchBauComplementPdf(bauComplementId);
        saveBlobToDisk(pdfBlob, pdfName);
        await wait(400);
        const details = await fetchBauComplementDetailsForFiles(bauComplementId);
        const files = extractAllFileEntriesBauComplement(details);
        if (!files.length) return;
        for (let index = 0; index < files.length; index += 1) {
          const file = files[index];
          await downloadFileByUrl(file.url, file.name);
          await wait(300);
        }
      } catch (_) {
        try {
          const { blob, filename } = await fetchBauComplementPdf(bauComplementId);
          saveBlobToDisk(blob, filename);
        } catch (error) {
          toast({
            component: ToastificationContent,
            props: {
              title: vm.$t("errors.download_failed") || "No se pudo descargar",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }
      }
    };

    const exportExcel = async () => {
      try {
        buttonExport.value = true;
        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          sort_by: sortBy.value,
          sort_desc: isSortDirDesc.value ? 1 : 0,
          search: searchQuery.value,
          units: filters.value.units,
          users: filters.value.users,
          countries: filters.value.countries,
          municipalities: filters.value.municipalities,
          status_form: filters.value.status,
          typed: filters.value.formType,
          responsable: filters.value.responsable,
          assigned: filters.value.assigned,
          date_from: filters.value.startDate,
          date_to: filters.value.endDate,
          rems: filters.value.rems,
          sirh: filters.value.sirh,
          action: "export",
        };
        const response = await axios.get("/v1/bau-complements", { params, responseType: "blob" });
        const contentDisposition = response.headers["content-disposition"];
        let filename = "bau_complements.xlsx";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
          if (match) filename = decodeURIComponent(match[1]);
        }
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.message || "Error al exportar",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        buttonExport.value = false;
      }
    };

    const getUserPrimary = (value) => {
      if (!value) return "-";
      if (typeof value === "string") return value || "-";
      return value.name || value.username || value.email || "-";
    };
    const getUserSecondary = (value) => {
      if (!value || typeof value === "string") return "";
      return value.username || value.email || "";
    };

    const update = (value) => {
      currentPage.value = value;
    };

    const openAdd = () => {
      vm.$root.$emit("bv::show::modal", "bau-complement-wizard");
    };

    const onWizardComplete = () => {
      refetchData();
    };

    const onModalClose = () => {
      refetchData();
    };

    onMounted(() => {
      openFromNotification();
    });

    return {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      unitOptions,
      userOptions,
      filters,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      countryOptions,
      municipalityOptions,
      statusOptions,
      formTypeOptions,
      update,
      openAdd,
      onWizardComplete,
      onModalClose,
      showEditModal,
      handleOpen,
      confirmDelete,
      formatStatus,
      normalizeStatusKey,
      canShowDownload,
      getFormType,
      getDisplayValue,
      canShowCancelAction,
      getUserPrimary,
      getUserSecondary,
      modalId,
      modalSnapshot,
      modalSources,
      exportExcel,
      buttonExport,
      downloadPdfAndFilesLinear,
      canShowStartForm,
      openStartFormFromRow,
      wizardPrefill,
      onWizardClosed,
    };
  },
};
</script>
